// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2026 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { PageStyle } from "@insite/mobius/Page";
import { slideshowStyles } from "@stoneage/Overrides/Widgets/Basic/Slideshow";
import { color, xScreen } from "@stoneage/XDL/Utilities";
import { css } from "styled-components";
import { IconWrapper } from "@insite/mobius/Icon";
import { ButtonPresentationProps } from "@insite/mobius/Button";

const arrowStyles: ButtonPresentationProps = {
    css: css`
        padding: 0;
        border: 0;
        background: transparent;
        display: flex;
        align-items: center;
        justify-content: center;
        min-height: initial;
        width: initial;

        & > ${IconWrapper} {
            color: ${color("common.white")};
        }

        &:hover,
        &:focus {
            background-color: transparent;
            border: 0;
            outline: none;
        }

        &:hover > ${IconWrapper} {
            color: ${color("primary")};
        }
    `,
};

slideshowStyles.slideshowWrapper = {
    css: css`
        position: relative;
    `,
};

slideshowStyles.nextArrowButton = {
    ...arrowStyles,
};

slideshowStyles.prevArrowButton = {
    ...arrowStyles,
};

slideshowStyles.pauseButton = {
    ...arrowStyles,
};

slideshowStyles.iconProps = {
    size: 14,
};

slideshowStyles.slideIndicatorWrapper = {
    css: css`
        position: absolute;
        left: 50%;
        transform: translateX(-50%);
        bottom: 12px;
        display: flex;
        justify-content: center;
        gap: 12px;
        padding: 5px 8px;
        background-color: ${color("common.black")};

        ${xScreen("lte", "md")} {
            position: relative;
            padding-top: 48px;
            padding-bottom: 24px;
        }
    `,
};

slideshowStyles.dotsContainerWrapper = {
    css: css`
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 4px;
    `,
};

slideshowStyles.dotButton = {
    css: css`
        min-height: initial;
        height: 8px;
        width: 8px;
        aspect-ratio: 1;
        border-radius: 50%;
        padding: 0;
        border: 1px solid ${color("common.white")};
        background: transparent;

        &:focus,
        &:active {
            border-color: ${color("common.white")};
            background: ${color("common.white")};
            outline: none;
        }

        &:hover:not([aria-selected="true"]) {
            border-color: ${color("primary")};
            background: ${color("primary")};
        }
    `,
};

slideshowStyles.slideCenteringWrapperStyles = {
    css: css`
        max-width: 858px;
        ${xScreen("lte", "md")} {
            max-width: 100%;
        }
    `,
};

slideshowStyles.slideContentWrapper = {
    css: css`
        display: flex;
        flex: 0 0 100%;
        position: relative;
        color: white;

        ${xScreen("lte", "md")} {
            flex-direction: column;
            min-height: auto;
            ${({ contentBackgroundColorOnMobile }: any) =>
                contentBackgroundColorOnMobile &&
                css`
                    &,
                    ${PageStyle} {
                        background-color: ${contentBackgroundColorOnMobile};
                    }
                `}
        }
    `,
};

slideshowStyles.slideImageWrapper = {
    css: css`
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        z-index: 0;
        overflow: hidden;
        & div {
            width: 100%;
            height: 100%;
        }

        ${xScreen("lte", "md")} {
            position: static;
            ${({ maxHeightMediaSourceOnTablet }: any) =>
                maxHeightMediaSourceOnTablet &&
                css`
                    max-height: ${maxHeightMediaSourceOnTablet};
                `};
        }
        ${xScreen("lte", "xs")} {
            position: static;
            ${({ maxHeightMediaSourceOnMobile }: any) =>
                maxHeightMediaSourceOnMobile &&
                css`
                    max-height: ${maxHeightMediaSourceOnMobile};
                `};
        }
    `,
};

slideshowStyles.videoWrapper = {
    css: css`
        height: 100%;
        width: 100%;
        position: absolute;
        z-index: 1;

        ${xScreen("lte", "md")} {
            position: static;
            ${({ maxHeightMediaSourceOnTablet }: any) =>
                maxHeightMediaSourceOnTablet &&
                css`
                    max-height: ${maxHeightMediaSourceOnTablet};
                `};
        }
        ${xScreen("lte", "xs")} {
            position: static;
            ${({ maxHeightMediaSourceOnMobile }: any) =>
                maxHeightMediaSourceOnMobile &&
                css`
                    max-height: ${maxHeightMediaSourceOnMobile};
                `};
        }
    `,
};

slideshowStyles.video = {
    videoWrapper: {
        css: css`
            display: flex;
            video {
                object-fit: cover;
            }
        `,
    },
};
